import { useState, useCallback } from 'react';
import { useToastNotifications } from '@/lib/hooks/use-toast-notifications';
import { useGlobalSpinner } from './useGlobalSpinner';
import { useI18n } from '@/i18n/I18nProvider';
import { authenticatedFetcher } from '@/lib/swr';
import { downloadBase64File } from '@/lib/helpers';
import type { ApiResponse } from '@/types/api-response';
import { PP_BASE_URL } from '@/lib/env';

export interface DownloadPartnershipCharterResponse {
  academicyear?: string;
  studentNumber?: string;
  attachment01?: string | null;
  datetime?: string | null;
}

interface DownloadCharterOptions {
  studentNumber?: string | null;
  academicYear?: string | null;
}

export function useDownloadCharter() {
  const [isDownloading, setIsDownloading] = useState(false);
  const { showSpinner, hideSpinner } = useGlobalSpinner();
  const { t } = useI18n();
  const toast = useToastNotifications();

  const downloadCharter = useCallback(
    async ({ studentNumber, academicYear }: DownloadCharterOptions) => {
      if (!studentNumber) {
        toast.warning(t.notAvailable, t.noSignedCharterAvailable);
        return;
      }

      setIsDownloading(true);
      showSpinner();

      try {
        let charterAttachment: string | null = null;

        const downloadUrl = `${PP_BASE_URL}/charter/download?studentNumber=${encodeURIComponent(studentNumber)}${
          academicYear ? `&academicyear=${encodeURIComponent(academicYear)}` : ''
        }`;

        try {
          const res = await authenticatedFetcher<ApiResponse<DownloadPartnershipCharterResponse>>(downloadUrl);
          charterAttachment = res?.data?.attachment01 ?? null;
        } catch (err: any) {
          if (err?.status !== 404) {
            console.error('Failed to fetch charter download:', err);
          }
        }

        if (!charterAttachment) {
          toast.warning(t.notAvailable, t.noSignedCharterAvailable);
          return;
        }

        downloadBase64File(charterAttachment, `partnership-charter-${studentNumber}.pdf`, 'application/pdf');

        toast.success(t.downloaded, t.charterDownloadedSuccessfully);
      } catch (err) {
        console.error('Failed to download charter:', err);
        toast.error(t.error, t.failedToDownloadCharter);
      } finally {
        setIsDownloading(false);
        hideSpinner();
      }
    },
    [showSpinner, hideSpinner, t, toast]
  );

  return {
    downloadCharter,
    isDownloading,
  };
}
